/**
 * queryKeys - Central, typed query-key factory for TanStack Query.
 *
 * Every cached read in the app derives its key from here so that:
 *	 - Keys are spelled consistently (no stringly-typed drift between files)
 *	 - Invalidation is precise: invalidate `qk.workOrders.all` to refresh every
 *	   work-order query, or `qk.workOrders.detail(id)` for just one.
 *
 * Convention (mirrors the TanStack "query key factory" community pattern):
 *	 qk.<entity>.all						-> ['<entity>']                       (root; invalidates everything for the entity)
 *	 qk.<entity>.lists()					-> ['<entity>', 'list']               (all list variants)
 *	 qk.<entity>.list(params)				-> ['<entity>', 'list', params]       (one filtered/paginated list)
 *	 qk.<entity>.details()					-> ['<entity>', 'detail']             (all detail variants)
 *	 qk.<entity>.detail(id)					-> ['<entity>', 'detail', id]         (one record)
 *
 * `params` is the same plain object pages already build for the API (status,
 * page, date_from, …). TanStack hashes it deterministically, so two renders with
 * equal params share a cache entry.
 */

/** Loosely-typed params bag — matches the Record<string,string> pages pass to services. */
export type QueryParams = Record<string, unknown> | undefined;

/** Helper that builds the standard list/detail key set for a named entity. */
function entityKeys(entity: string) {
	const all = [entity] as const;
	return {
		all,
		lists: () => [...all, 'list'] as const,
		list: (params?: QueryParams) => [...all, 'list', params ?? {}] as const,
		details: () => [...all, 'detail'] as const,
		detail: (id: string | number) => [...all, 'detail', String(id)] as const,
	};
}

export const qk = {
	workOrders: entityKeys('workOrders'),
	projectWorkOrders: entityKeys('projectWorkOrders'),
	inspections: entityKeys('inspections'),
	recurrenceSeries: entityKeys('recurrenceSeries'),
	scheduledEvents: entityKeys('scheduledEvents'),
	buildings: entityKeys('buildings'),
	companies: entityKeys('companies'),
	contacts: entityKeys('contacts'),
	locations: entityKeys('locations'),
	users: entityKeys('users'),
	apiKeys: entityKeys('apiKeys'),
	businessLines: entityKeys('businessLines'),
	checklistTemplates: entityKeys('checklistTemplates'),
	checklistInstances: entityKeys('checklistInstances'),
	photoTypes: entityKeys('photoTypes'),
	estimateApprovals: entityKeys('estimateApprovals'),

	/** Dashboard metrics keyed by the selected period (e.g. '7d', '30d', 'qtd'). */
	dashboard: {
		all: ['dashboard'] as const,
		metrics: (period?: string) => ['dashboard', 'metrics', period ?? 'default'] as const,
	},

	/** Work-order / inspection workflow state (checklist + sign-off) per record id. */
	workOrderWorkflow: (id: string | number) => ['workOrderWorkflow', String(id)] as const,
	inspectionWorkflow: (id: string | number) => ['inspectionWorkflow', String(id)] as const,

	/** HubSpot integration connection + sync status (singleton, no id). */
	hubspot: {
		all: ['hubspot'] as const,
		connection: () => ['hubspot', 'connection'] as const,
		sync: () => ['hubspot', 'sync'] as const,
	},
} as const;
